import React, { useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { AXIOS_INSTANCE } from '../../services/api-client';
import { useToasts } from '../../stores/useToasts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Page,
  PageBody,
  PageHeader,
  Select,
  SelectOption,
  Spinner,
} from '../../components/ui';

type ProviderType = 'postmark' | 'smtp';

interface EmailConfigResponse {
  provider_type: string;
  is_enabled: boolean;
  from_email: string | null;
  from_name: string | null;
  reply_to: string | null;
  message_stream: string | null;
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_use_tls: boolean | null;
  api_token_set: boolean;
  server_token_set: boolean;
  webhook_secret_set: boolean;
  smtp_username_set: boolean;
  smtp_password_set: boolean;
  verified_sender_domains: string[];
  dns_status: Record<string, any> | null;
  last_health_check: string | null;
  last_health_error: string | null;
  configured_by: number | null;
  updated_at: string | null;
}

interface DomainVerificationStatus {
  domain: string;
  spf_verified: boolean | null;
  dkim_verified: boolean | null;
  return_path_verified: boolean | null;
  dmarc_observed: boolean | null;
}

interface DomainVerificationResponse {
  provider_type: string | null;
  domains: DomainVerificationStatus[];
  last_checked_at: string | null;
  error_message: string | null;
}

interface EmailStatusResponse {
  is_configured: boolean;
  is_enabled: boolean;
  is_healthy: boolean;
  provider_type: string | null;
  recent_send_count: number;
  recent_failure_count: number;
  recent_bounce_count: number;
}

interface SendLogEntry {
  id: number;
  purpose: string;
  to_email: string;
  subject: string;
  status: string;
  provider_message_id: string | null;
  error_message: string | null;
  queued_at: string;
  sent_at: string | null;
  delivered_at: string | null;
}

interface SendLogListResponse {
  items: SendLogEntry[];
  total: number;
}

interface ConfigFormState {
  provider_type: ProviderType;
  is_enabled: boolean;
  from_email: string;
  from_name: string;
  reply_to: string;
  message_stream: string;
  api_token: string;
  server_token: string;
  webhook_secret: string;
  smtp_host: string;
  smtp_port: string;
  smtp_use_tls: boolean;
  smtp_username: string;
  smtp_password: string;
  verified_sender_domains: string;
}

function getApiErrorMessage(error: any, fallback: string): string {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((item: any) => (typeof item?.msg === 'string' ? item.msg : JSON.stringify(item)))
      .join(' | ');
  }
  return fallback;
}

const INITIAL_FORM: ConfigFormState = {
  provider_type: 'postmark',
  is_enabled: false,
  from_email: '',
  from_name: '',
  reply_to: '',
  message_stream: 'outbound',
  api_token: '',
  server_token: '',
  webhook_secret: '',
  smtp_host: '',
  smtp_port: '587',
  smtp_use_tls: true,
  smtp_username: '',
  smtp_password: '',
  verified_sender_domains: '',
};

export function EmailIntegrationPage() {
  const { push } = useToasts();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isRefreshingLogs, setIsRefreshingLogs] = useState(false);

  const [config, setConfig] = useState<EmailConfigResponse | null>(null);
  const [status, setStatus] = useState<EmailStatusResponse | null>(null);
  const [form, setForm] = useState<ConfigFormState>(INITIAL_FORM);
  const [logs, setLogs] = useState<SendLogEntry[]>([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [testEmail, setTestEmail] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [purposeFilter, setPurposeFilter] = useState('all');
  const [domainStatus, setDomainStatus] = useState<DomainVerificationResponse | null>(null);
  const [isVerifyingDomains, setIsVerifyingDomains] = useState(false);
  const [resendingLogId, setResendingLogId] = useState<number | null>(null);

  const setField = <K extends keyof ConfigFormState>(key: K, value: ConfigFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const loadConfig = async () => {
    const response = await AXIOS_INSTANCE.get<EmailConfigResponse>('/api/v1/platform-admin/email/config');
    const cfg = response.data;
    setConfig(cfg);
    setForm({
      provider_type: (cfg.provider_type as ProviderType) || 'postmark',
      is_enabled: cfg.is_enabled,
      from_email: cfg.from_email || '',
      from_name: cfg.from_name || '',
      reply_to: cfg.reply_to || '',
      message_stream: cfg.message_stream || 'outbound',
      api_token: '',
      server_token: '',
      webhook_secret: '',
      smtp_host: cfg.smtp_host || '',
      smtp_port: cfg.smtp_port ? String(cfg.smtp_port) : '587',
      smtp_use_tls: cfg.smtp_use_tls ?? true,
      smtp_username: '',
      smtp_password: '',
      verified_sender_domains: (cfg.verified_sender_domains || []).join(', '),
    });
  };

  const loadStatus = async () => {
    const response = await AXIOS_INSTANCE.get<EmailStatusResponse>('/api/v1/platform-admin/email/status');
    setStatus(response.data);
  };

  const loadLogs = async () => {
    const params: Record<string, string | number> = { page: 1, page_size: 20 };
    if (statusFilter !== 'all') params.status = statusFilter;
    if (purposeFilter !== 'all') params.purpose = purposeFilter;
    const response = await AXIOS_INSTANCE.get<SendLogListResponse>('/api/v1/platform-admin/email/send-log', { params });
    setLogs(response.data.items || []);
    setTotalLogs(response.data.total || 0);
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        await Promise.all([loadConfig(), loadStatus(), loadLogs()]);
      } catch (error: any) {
        push({ kind: 'error', message: getApiErrorMessage(error, 'Failed to load platform email settings') });
      } finally {
        setIsLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoading) return;
    const refresh = async () => {
      setIsRefreshingLogs(true);
      try {
        await loadLogs();
      } catch (error: any) {
        push({ kind: 'error', message: getApiErrorMessage(error, 'Failed to refresh logs') });
      } finally {
        setIsRefreshingLogs(false);
      }
    };
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, purposeFilter]);

  const currentProviderTokensSummary = (() => {
    if (!config) return 'No configuration loaded';
    if (form.provider_type === 'postmark') {
      return `${config.server_token_set || config.api_token_set ? 'Configured' : 'Missing'} tokens`;
    }
    return `${config.smtp_username_set && config.smtp_password_set ? 'Configured' : 'Missing'} SMTP credentials`;
  })();

  const handleVerifyDomains = async () => {
    setIsVerifyingDomains(true);
    try {
      const response = await AXIOS_INSTANCE.post<DomainVerificationResponse>(
        '/api/v1/platform-admin/email/domains/verify',
      );
      setDomainStatus(response.data);
      if (response.data.error_message) {
        push({ kind: 'error', message: response.data.error_message });
      } else {
        push({ kind: 'success', message: 'Domain verification refreshed' });
      }
    } catch (error: any) {
      push({ kind: 'error', message: getApiErrorMessage(error, 'Failed to verify domains') });
    } finally {
      setIsVerifyingDomains(false);
    }
  };

  const handleResend = async (logId: number) => {
    setResendingLogId(logId);
    try {
      await AXIOS_INSTANCE.post(`/api/v1/platform-admin/email/send-log/${logId}/resend`);
      push({ kind: 'success', message: 'Resend queued' });
      await loadLogs();
    } catch (error: any) {
      push({ kind: 'error', message: getApiErrorMessage(error, 'Failed to queue resend') });
    } finally {
      setResendingLogId(null);
    }
  };

  const handleSave = async () => {
    if (!form.from_email.trim() || !form.from_name.trim()) {
      push({ kind: 'error', message: 'From email and from name are required' });
      return;
    }

    const verifiedDomains = form.verified_sender_domains
      .split(/[,\s]+/)
      .map((d) => d.trim().toLowerCase())
      .filter(Boolean);

    const payload: Record<string, any> = {
      provider_type: form.provider_type,
      is_enabled: form.is_enabled,
      from_email: form.from_email.trim(),
      from_name: form.from_name.trim(),
      reply_to: form.reply_to.trim() || null,
      verified_sender_domains: verifiedDomains,
    };

    if (form.provider_type === 'postmark') {
      payload.message_stream = form.message_stream.trim() || 'outbound';
      if (form.api_token.trim()) payload.api_token = form.api_token.trim();
      if (form.server_token.trim()) payload.server_token = form.server_token.trim();
      if (form.webhook_secret.trim()) payload.webhook_secret = form.webhook_secret.trim();
    } else {
      payload.smtp_host = form.smtp_host.trim();
      payload.smtp_port = Number(form.smtp_port || 587);
      payload.smtp_use_tls = form.smtp_use_tls;
      if (form.smtp_username.trim()) payload.smtp_username = form.smtp_username.trim();
      if (form.smtp_password.trim()) payload.smtp_password = form.smtp_password.trim();
    }

    setIsSaving(true);
    try {
      await AXIOS_INSTANCE.put('/api/v1/platform-admin/email/config', payload);
      push({ kind: 'success', message: 'Platform email settings saved' });
      await Promise.all([loadConfig(), loadStatus()]);
    } catch (error: any) {
      push({ kind: 'error', message: getApiErrorMessage(error, 'Failed to save email settings') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestSend = async () => {
    if (!testEmail.trim()) {
      push({ kind: 'error', message: 'Enter a recipient email for the test send' });
      return;
    }
    setIsTesting(true);
    try {
      const response = await AXIOS_INSTANCE.post<{ success: boolean; message?: string }>(
        '/api/v1/platform-admin/email/test',
        { to_email: testEmail.trim() },
      );
      if (response.data.success) {
        push({ kind: 'success', message: 'Test email queued/sent successfully' });
      } else {
        push({ kind: 'error', message: response.data.message || 'Test send failed' });
      }
      await Promise.all([loadStatus(), loadLogs()]);
    } catch (error: any) {
      push({ kind: 'error', message: getApiErrorMessage(error, 'Failed to run test send') });
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <Page maxWidth="xl">
        <PageBody>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner size="sm" />
            Loading platform email settings...
          </div>
        </PageBody>
      </Page>
    );
  }

  return (
    <Page maxWidth="xl">
      <PageHeader
        title="Platform Email Settings"
        description="Configure delivery provider, sender defaults, health checks, and send logs."
        actions={
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="mr-2" /> : null}
            Save Settings
          </Button>
        }
      />
      <PageBody className="space-y-6 pb-10">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <EnvelopeIcon className="h-5 w-5 text-eliza-red" />
              Current Status
            </CardTitle>
            <CardDescription>Platform-level delivery health and 24h volume snapshot.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={status?.is_enabled ? 'success' : 'warning'}>
                {status?.is_enabled ? 'Enabled' : 'Disabled'}
              </Badge>
              <Badge variant={status?.is_healthy ? 'success' : 'warning'}>
                {status?.is_healthy ? 'Healthy' : 'Unhealthy'}
              </Badge>
              <Badge variant="info">Provider: {status?.provider_type || 'not-configured'}</Badge>
            </div>
            <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
              <div className="rounded-lg border border-gray-200 p-3 dark:border-dark-border">
                <div className="text-gray-500 dark:text-gray-400">Recent sends (24h)</div>
                <div className="mt-1 text-lg font-semibold text-charcoal dark:text-gray-100">
                  {status?.recent_send_count ?? 0}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 p-3 dark:border-dark-border">
                <div className="text-gray-500 dark:text-gray-400">Failures (24h)</div>
                <div className="mt-1 text-lg font-semibold text-charcoal dark:text-gray-100">
                  {status?.recent_failure_count ?? 0}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 p-3 dark:border-dark-border">
                <div className="text-gray-500 dark:text-gray-400">Bounces (24h)</div>
                <div className="mt-1 text-lg font-semibold text-charcoal dark:text-gray-100">
                  {status?.recent_bounce_count ?? 0}
                </div>
              </div>
            </div>
            {config?.last_health_error && (
              <Alert variant="warning">{config.last_health_error}</Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Provider Configuration</CardTitle>
            <CardDescription>Configure Postmark (hosted default) or SMTP for customer deployments.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Provider Type</Label>
                <Select value={form.provider_type} onValueChange={(value) => setField('provider_type', value as ProviderType)}>
                  <SelectOption value="postmark">Postmark</SelectOption>
                  <SelectOption value="smtp">SMTP</SelectOption>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Delivery Enabled</Label>
                <Select
                  value={form.is_enabled ? 'true' : 'false'}
                  onValueChange={(value) => setField('is_enabled', value === 'true')}
                >
                  <SelectOption value="true">Enabled</SelectOption>
                  <SelectOption value="false">Disabled</SelectOption>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Default From Email</Label>
                <Input
                  value={form.from_email}
                  onChange={(e) => setField('from_email', e.target.value)}
                  placeholder="noreply@elizaaccelerate.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Default From Name</Label>
                <Input
                  value={form.from_name}
                  onChange={(e) => setField('from_name', e.target.value)}
                  placeholder="Eliza Accelerate"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reply-To (Optional)</Label>
              <Input
                value={form.reply_to}
                onChange={(e) => setField('reply_to', e.target.value)}
                placeholder="support@elizaaccelerate.com"
              />
            </div>

            {form.provider_type === 'postmark' ? (
              <div className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-dark-border">
                <div className="text-sm font-medium text-charcoal dark:text-gray-100">Postmark</div>
                <div className="space-y-2">
                  <Label>Message Stream</Label>
                  <Input
                    value={form.message_stream}
                    onChange={(e) => setField('message_stream', e.target.value)}
                    placeholder="outbound"
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>API Token (optional update)</Label>
                    <Input
                      type="password"
                      value={form.api_token}
                      onChange={(e) => setField('api_token', e.target.value)}
                      placeholder={config?.api_token_set ? 'Configured' : 'Enter API token'}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Server Token (optional update)</Label>
                    <Input
                      type="password"
                      value={form.server_token}
                      onChange={(e) => setField('server_token', e.target.value)}
                      placeholder={config?.server_token_set ? 'Configured' : 'Enter server token'}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Webhook Secret (optional update)</Label>
                  <Input
                    type="password"
                    value={form.webhook_secret}
                    onChange={(e) => setField('webhook_secret', e.target.value)}
                    placeholder={config?.webhook_secret_set ? 'Configured' : 'Enter webhook secret'}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-dark-border">
                <div className="text-sm font-medium text-charcoal dark:text-gray-100">SMTP</div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>SMTP Host</Label>
                    <Input
                      value={form.smtp_host}
                      onChange={(e) => setField('smtp_host', e.target.value)}
                      placeholder="smtp.company.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Port</Label>
                    <Input
                      value={form.smtp_port}
                      onChange={(e) => setField('smtp_port', e.target.value)}
                      placeholder="587"
                    />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>SMTP Username (optional update)</Label>
                    <Input
                      value={form.smtp_username}
                      onChange={(e) => setField('smtp_username', e.target.value)}
                      placeholder={config?.smtp_username_set ? 'Configured' : 'Enter username'}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Password (optional update)</Label>
                    <Input
                      type="password"
                      value={form.smtp_password}
                      onChange={(e) => setField('smtp_password', e.target.value)}
                      placeholder={config?.smtp_password_set ? 'Configured' : 'Enter password'}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>TLS</Label>
                  <Select
                    value={form.smtp_use_tls ? 'true' : 'false'}
                    onValueChange={(value) => setField('smtp_use_tls', value === 'true')}
                  >
                    <SelectOption value="true">Use TLS</SelectOption>
                    <SelectOption value="false">No TLS</SelectOption>
                  </Select>
                </div>
              </div>
            )}

            <Alert variant="info">{currentProviderTokensSummary}</Alert>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Verified Sender Domains</CardTitle>
            <CardDescription>
              Tenants can only override the From Email if its domain matches one of these (or any subdomain).
              Leave blank to disable enforcement.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label>Domains (comma or whitespace separated)</Label>
              <Input
                value={form.verified_sender_domains}
                onChange={(e) => setField('verified_sender_domains', e.target.value)}
                placeholder="elizaaccelerate.com, acme.com"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleVerifyDomains} disabled={isVerifyingDomains}>
                {isVerifyingDomains ? <Spinner size="sm" className="mr-2" /> : null}
                Verify SPF / DKIM with provider
              </Button>
              {domainStatus?.last_checked_at ? (
                <span className="text-xs text-gray-500">
                  Last checked {new Date(domainStatus.last_checked_at).toLocaleString()}
                </span>
              ) : null}
            </div>
            {domainStatus?.error_message ? (
              <Alert variant="warning">{domainStatus.error_message}</Alert>
            ) : null}
            {domainStatus && domainStatus.domains.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-dark-border">
                <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-dark-border">
                  <thead className="bg-gray-50 dark:bg-dark-surface-2">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Domain</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">SPF</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">DKIM</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Return-Path</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white dark:divide-dark-border dark:bg-dark-surface">
                    {domainStatus.domains.map((d) => (
                      <tr key={d.domain}>
                        <td className="px-3 py-2 font-mono text-xs text-charcoal dark:text-gray-100">
                          {d.domain || '-'}
                        </td>
                        {[d.spf_verified, d.dkim_verified, d.return_path_verified].map((flag, idx) => (
                          <td key={idx} className="px-3 py-2">
                            {flag === null ? (
                              <span className="text-gray-400">n/a</span>
                            ) : flag ? (
                              <span className="inline-flex items-center gap-1 text-green-600">
                                <CheckCircleIcon className="h-4 w-4" /> verified
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-red-500">
                                <XCircleIcon className="h-4 w-4" /> missing
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Test Send</CardTitle>
            <CardDescription>Send a verification email through the configured provider.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 md:grid-cols-[1fr_auto]">
              <Input
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com"
              />
              <Button onClick={handleTestSend} disabled={isTesting}>
                {isTesting ? <Spinner size="sm" className="mr-2" /> : <PaperAirplaneIcon className="mr-2 h-4 w-4" />}
                Send Test Email
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClockIcon className="h-5 w-5 text-gray-500" />
              Send Log
            </CardTitle>
            <CardDescription>Latest metadata-only outbound events across tenants.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectOption value="all">All statuses</SelectOption>
                <SelectOption value="sent">Sent</SelectOption>
                <SelectOption value="delivered">Delivered</SelectOption>
                <SelectOption value="failed">Failed</SelectOption>
                <SelectOption value="bounced">Bounced</SelectOption>
              </Select>
              <Select value={purposeFilter} onValueChange={setPurposeFilter}>
                <SelectOption value="all">All purposes</SelectOption>
                <SelectOption value="invite">Invite</SelectOption>
                <SelectOption value="password_reset">Password Reset</SelectOption>
                <SelectOption value="notification">Notification</SelectOption>
                <SelectOption value="workflow">Workflow</SelectOption>
                <SelectOption value="test">Test</SelectOption>
              </Select>
              <Button variant="outline" onClick={loadLogs} disabled={isRefreshingLogs}>
                {isRefreshingLogs ? <Spinner size="sm" className="mr-2" /> : <ArrowPathIcon className="mr-2 h-4 w-4" />}
                Refresh
              </Button>
            </div>

            {logs.length === 0 ? (
              <Alert variant="info">No send log entries found for current filters.</Alert>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-dark-border">
                <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-dark-border">
                  <thead className="bg-gray-50 dark:bg-dark-surface-2">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Queued</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">To</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Purpose</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Message ID</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white dark:divide-dark-border dark:bg-dark-surface">
                    {logs.map((row) => {
                      const canResend =
                        row.status === 'failed' ||
                        row.status === 'bounced' ||
                        row.status === 'complaint';
                      return (
                        <tr key={row.id}>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300">
                            {new Date(row.queued_at).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-charcoal dark:text-gray-100">{row.to_email}</td>
                          <td className="px-3 py-2">
                            <Badge variant="default">{row.purpose}</Badge>
                          </td>
                          <td className="px-3 py-2">
                            {row.status === 'delivered' || row.status === 'sent' ? (
                              <span className="inline-flex items-center gap-1 text-green-600">
                                <CheckCircleIcon className="h-4 w-4" />
                                {row.status}
                              </span>
                            ) : row.status === 'failed' || row.status === 'bounced' ? (
                              <span className="inline-flex items-center gap-1 text-red-500">
                                <XCircleIcon className="h-4 w-4" />
                                {row.status}
                              </span>
                            ) : (
                              row.status
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-500">
                            {row.provider_message_id || '-'}
                          </td>
                          <td className="px-3 py-2">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!canResend || resendingLogId === row.id}
                              onClick={() => handleResend(row.id)}
                            >
                              {resendingLogId === row.id ? (
                                <Spinner size="sm" className="mr-2" />
                              ) : null}
                              Resend
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div className="text-xs text-gray-500 dark:text-gray-400">Total records: {totalLogs}</div>
          </CardContent>
        </Card>
      </PageBody>
    </Page>
  );
}

export default EmailIntegrationPage;
